import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Bot,
  CalendarClock,
  Columns3,
  Copy,
  ExternalLink,
  PlayCircle,
  Search,
  Users,
  Upload,
  User,
  Pencil,
  Trash2,
} from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import type { Application } from '@core/types';
import { applicationService } from '@core/supabase/services/applicationService';
import {
  aiInterviewSessionService,
  type AiInterviewSessionListRow,
} from '@core/supabase/services/aiInterviewSessionService';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { useToast } from '@shared/hooks/useToast';
import { ListOperationsDropdown } from '@shared/components/ui/ListOperationsDropdown';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import { listExportNotImplemented } from '@shared/lib/listExportStub';
import { DataTableDeleteConfirmModal } from '@shared/components/ui/DataTableDeleteConfirmModal';
import { StatusChip, statusDotColorFromHint } from '@shared/components/ui/StatusChip';
import { EmptyState } from '@shared/components/ui/EmptyState';
import { DataTableToolbar } from '@shared/components/ui/DataTableToolbar';
import { TablePagination } from '@shared/components/ui/TablePagination';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { AiInterviewScheduleWizardModal } from './AiInterviewScheduleWizardModal';
import { AiInterviewsConfigureColumnsModal } from './AiInterviewsConfigureColumnsModal';
import {
  loadAiInterviewsTablePolicy,
  type AiInterviewExtraColumnKey,
} from './aiInterviewsListPreferences';
import { GenericManageRightsModal } from '@shared/components/list/GenericManageRightsModal';
import {
  resolveReviewerSessionStatus,
  reviewerListScoreDisplay,
  reviewerSessionDateIso,
  reviewerStatusChipLabel,
} from './aiInterviewReviewerUi';

function interviewPrepUrl(token: string): string {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}/interview/prep/${token}`;
}

function formatPipelineSlotStatus(raw: string | undefined): string {
  if (!raw) return '—';
  if (raw === 'completed') return 'Conducted';
  return raw.replace(/_/g, ' ');
}

function exportSessionsCsv(rows: AiInterviewSessionListRow[]) {
  const header = [
    'Candidate',
    'Email',
    'Session date',
    'Score',
    'Reviewer status',
    'Round',
    'Language',
    'Raw status',
    'Scheduled by',
    'Created',
    'Expires',
    'Prep link',
  ];
  const lines = [
    header,
    ...rows.map((r) => [
      r.candidate?.full_name ?? '',
      r.candidate?.email ?? '',
      reviewerSessionDateIso(r),
      reviewerListScoreDisplay(r.assessment_scores, r.assessed_at),
      reviewerStatusChipLabel(resolveReviewerSessionStatus(r.status, r.assessed_at, r.assessment_scores)),
      r.session_type,
      r.language,
      r.status,
      r.initiator?.display_name?.trim() || r.initiator?.email || '',
      r.created_at,
      r.expires_at ?? '',
      interviewPrepUrl(r.access_token),
    ]),
  ];
  const csv = lines.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ai-interviews-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function AiInterviewsTableSkeleton() {
  return (
    <div className="card overflow-hidden p-0" aria-busy="true" aria-label="Loading AI interviews">
      <div className="ggj-data-table-wrap min-w-0">
        <table className="ggj-data-table ggj-data-table--wide ggj-data-table--candidates">
          <thead>
            <tr>
              <th className="w-10 px-4 py-3" />
              <th className="py-3">Candidate</th>
              <th className="py-3 whitespace-nowrap">Session date</th>
              <th className="py-3 whitespace-nowrap">Score</th>
              <th className="py-3 whitespace-nowrap">Status</th>
              <th className="py-3 whitespace-nowrap">Action</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }).map((_, i) => (
              <tr key={i}>
                <td className="px-4 py-3 w-10">
                  <div className="h-4 w-4 animate-pulse rounded vscode-bg-tertiary" />
                </td>
                <td className="py-3">
                  <div className="h-4 w-48 max-w-full animate-pulse rounded vscode-bg-tertiary" />
                  <div className="mt-2 h-3 w-36 max-w-full animate-pulse rounded vscode-bg-tertiary" />
                </td>
                <td className="py-3">
                  <div className="h-4 w-28 animate-pulse rounded vscode-bg-tertiary" />
                </td>
                <td className="py-3">
                  <div className="h-4 w-10 animate-pulse rounded vscode-bg-tertiary" />
                </td>
                <td className="py-3">
                  <div className="h-6 w-24 rounded-full animate-pulse vscode-bg-tertiary" />
                </td>
                <td className="py-3">
                  <div className="h-8 w-24 animate-pulse rounded vscode-bg-tertiary" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AiInterviewsPage() {
  const { user } = useAuth();
  const perms = usePermissions();
  const { toast, showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  /** Hub is a single list + summary — remove legacy `?tab=` from older hub navigation. */
  useEffect(() => {
    if (!searchParams.get('tab')) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('tab');
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  const orgId = user?.orgId;
  const [rows, setRows] = useState<AiInterviewSessionListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copyMsg, setCopyMsg] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [configureOpen, setConfigureOpen] = useState(false);
  const [manageRightsOpen, setManageRightsOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [tablePolicy, setTablePolicy] = useState(() =>
    orgId ? loadAiInterviewsTablePolicy(orgId) : { extraColumns: [] as AiInterviewExtraColumnKey[] }
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const headerCheckRef = useRef<HTMLInputElement>(null);
  const [applications, setApplications] = useState<Application[]>([]);

  useEffect(() => {
    if (orgId) setTablePolicy(loadAiInterviewsTablePolicy(orgId));
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    applicationService
      .list(orgId, { limit: 800 })
      .then(setApplications)
      .catch(() => setApplications([]));
  }, [orgId]);

  const load = useCallback(async () => {
    if (!orgId) return;
    setError(null);
    setLoading(true);
    try {
      const list = await aiInterviewSessionService.listForOrg(orgId, 500);
      setRows(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load sessions.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rbacRows = useMemo(() => {
    if (perms.isOwner || perms.isAdmin) return rows;
    const uid = user?.orgUserId;
    if (!uid) return rows;
    return rows.filter((r) => {
      const creator = r.created_by_org_user_id;
      return creator == null || creator === uid;
    });
  }, [rows, perms.isOwner, perms.isAdmin, user?.orgUserId]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rbacRows;
    return rbacRows.filter((r) => {
      const name = (r.candidate?.full_name ?? '').toLowerCase();
      const email = (r.candidate?.email ?? '').toLowerCase();
      const st = (r.status ?? '').toLowerCase();
      const lang = (r.language ?? '').toLowerCase();
      const recruiter = `${r.initiator?.display_name ?? ''} ${r.initiator?.email ?? ''}`.toLowerCase();
      const scoreStr = reviewerListScoreDisplay(r.assessment_scores, r.assessed_at).toLowerCase();
      const chip = reviewerStatusChipLabel(
        resolveReviewerSessionStatus(r.status, r.assessed_at, r.assessment_scores)
      ).toLowerCase();
      const sessionWhen = formatDateTimeGmt(reviewerSessionDateIso(r)).toLowerCase();
      return (
        name.includes(q) ||
        email.includes(q) ||
        st.includes(q) ||
        lang.includes(q) ||
        recruiter.includes(q) ||
        scoreStr.includes(q) ||
        chip.includes(q) ||
        sessionWhen.includes(q)
      );
    });
  }, [rbacRows, search]);

  const totalItems = filteredRows.length;
  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [search, pageSize]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalItems / pageSize) || 1);
    if (page > maxPage) setPage(maxPage);
  }, [totalItems, pageSize, page]);

  useEffect(() => {
    if (headerCheckRef.current) {
      const allPage = pagedRows.length > 0 && pagedRows.every((r) => selectedIds.has(r.id));
      const somePage = pagedRows.some((r) => selectedIds.has(r.id));
      headerCheckRef.current.indeterminate = somePage && !allPage;
    }
  }, [pagedRows, selectedIds]);

  const hasExtra = (k: AiInterviewExtraColumnKey) => tablePolicy.extraColumns.includes(k);

  async function copyLink(token: string) {
    const url = interviewPrepUrl(token);
    try {
      await navigator.clipboard.writeText(url);
      setCopyMsg('Copied interview link.');
      window.setTimeout(() => setCopyMsg(null), 2500);
    } catch {
      setCopyMsg('Copy failed — select the URL manually.');
    }
  }

  async function handleDelete(ids: string[]) {
    if (!orgId) return;
    await aiInterviewSessionService.deleteByIds(orgId, ids);
    setSelectedIds(new Set());
    await load();
  }

  function handleLaunchInterviews() {
    const toLaunch = filteredRows.filter(
      (r) => selectedIds.has(r.id) && r.status !== 'completed' && r.status !== 'cancelled',
    );
    if (toLaunch.length === 0) {
      setCopyMsg('No active sessions to launch — completed or cancelled sessions cannot be reopened.');
      window.setTimeout(() => setCopyMsg(null), 3500);
      return;
    }
    for (const r of toLaunch) {
      const url = `${interviewPrepUrl(r.access_token)}?popup=1`;
      window.open(url, `ggj_interview_${r.id}`, 'width=1200,height=820');
    }
  }

  useEffect(() => {
    function onPopupMessage(e: MessageEvent) {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === 'ggj_interview_done') {
        const sid = typeof e.data.sessionRowId === 'string' ? e.data.sessionRowId : '';
        if (sid) {
          navigate(`/ai-interviews/${sid}?tab=review`);
        } else {
          void load();
        }
      }
    }
    window.addEventListener('message', onPopupMessage);
    return () => window.removeEventListener('message', onPopupMessage);
  }, [navigate, load]);

  return (
    <div className="w-full min-w-0 mx-auto max-w-none">
      {toast}
      <PageHeader
        variant="minimal"
        breadcrumb={[{ label: 'Data', href: '/candidates' }, { label: 'AI interviews' }]}
        breadcrumbRowEnd={
          <ListOperationsDropdown
            objectHistoryHref={objectHistoryUrl('ai_interviews')}
            onExport={(fmt, scope) => {
              if (fmt !== 'csv') {
                listExportNotImplemented('AI interviews', fmt, scope);
                return;
              }
              let data = filteredRows.length > 0 ? filteredRows : rows;
              if (scope === 'selection') {
                if (selectedIds.size === 0) {
                  showToast({ title: 'Select rows to export, or use CSV — all under Operations.', kind: 'error' });
                  return;
                }
                data = data.filter((r) => selectedIds.has(r.id));
              }
              exportSessionsCsv(data);
            }}
            extraActions={[
              {
                id: 'configure',
                label: 'Configure',
                icon: <Columns3 className="vscode-text-secondary" strokeWidth={2} />,
                onClick: () => setConfigureOpen(true),
              },
              {
                id: 'import',
                label: 'Import',
                icon: <Upload className="vscode-text-secondary" strokeWidth={2} />,
                onClick: () =>
                  showToast({ title: 'Session import is not supported. Create sessions with Interview or from a candidate profile.', kind: 'error' }),
              },
              {
                id: 'rights',
                label: 'Manage Rights',
                icon: <User className="vscode-text-secondary" strokeWidth={2} />,
                onClick: () => setManageRightsOpen(true),
              },
            ]}
          />
        }
      />

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400 rounded-lg border border-red-500/30 bg-red-950/30 px-3 py-2 mb-4">
          {error}
        </p>
      )}

      {!orgId ? (
        <p className="text-sm vscode-text-secondary">Select an organization to view AI interviews.</p>
      ) : loading ? (
        <AiInterviewsTableSkeleton />
      ) : (
        <>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" aria-hidden />
            <input
              className="input pl-9"
              placeholder="Search by candidate, email, session date, score, status, recruiter, or language…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {rbacRows.length === 0 ? (
                <EmptyState
                  icon={Bot}
                  title={rows.length === 0 ? 'No AI interview sessions yet' : 'No AI interview sessions visible to you'}
                  description={
                    rows.length === 0
                      ? 'Use Interview in the toolbar, or create a link from a candidate’s AI interviews tab (with an eligible application).'
                      : 'Your role only lists sessions you scheduled (and legacy sessions without an owner). Organization owners and admins can see every session in the org.'
                  }
                  action={
                    perms.canEditCandidate ? (
                      <button type="button" className="btn-primary inline-flex items-center gap-2" onClick={() => setScheduleOpen(true)}>
                        <CalendarClock className="w-4 h-4" aria-hidden />
                        Interview
                      </button>
                    ) : (
                      <Link to="/candidates" className="btn-primary inline-flex items-center gap-2">
                        <Users className="w-4 h-4" aria-hidden />
                        Open candidates
                      </Link>
                    )
                  }
                />
              ) : (
                <div className="card overflow-hidden p-0">
                  <div className="relative">
                    <DataTableToolbar
                      hasTable
                      newLabel={perms.canEditCandidate ? 'Interview' : undefined}
                      newOnClick={perms.canEditCandidate ? () => setScheduleOpen(true) : undefined}
                    />
                    {copyMsg ? (
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-emerald-600 dark:text-emerald-400">
                        {copyMsg}
                      </span>
                    ) : null}
                  </div>
                  <DataTableDeleteConfirmModal
                    open={bulkDeleteOpen}
                    onClose={() => setBulkDeleteOpen(false)}
                    entityLabel="AI interview sessions"
                    count={selectedIds.size}
                    bodyExtra="This removes the session record and its transcript from your org. Shared links will stop working."
                    onConfirm={() =>
                      void handleDelete(Array.from(selectedIds)).then(() => setBulkDeleteOpen(false))
                    }
                    requireTypedConfirm="DELETE"
                  />
                  {selectedIds.size > 0 && perms.canEditCandidate && (
                    <div className="ggj-data-bulk-bar flex flex-wrap items-center gap-2 px-4 py-2">
                      <span className="text-sm font-medium vscode-text-primary">{selectedIds.size} selected</span>
                      <button
                        type="button"
                        className="btn-secondary inline-flex items-center gap-1 text-xs"
                        disabled={selectedIds.size !== 1}
                        onClick={() => {
                          const id = Array.from(selectedIds)[0];
                          if (id) navigate(`/ai-interviews/${id}?tab=review`);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </button>
                      <button
                        type="button"
                        className="btn-secondary inline-flex items-center gap-1 text-xs"
                        onClick={() => setBulkDeleteOpen(true)}
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </button>
                      {(() => {
                        const launchable = filteredRows.filter(
                          (r) => selectedIds.has(r.id) && r.status !== 'completed' && r.status !== 'cancelled',
                        ).length;
                        return (
                          <button
                            type="button"
                            className="btn-secondary inline-flex items-center gap-1 text-xs"
                            onClick={handleLaunchInterviews}
                            disabled={launchable === 0}
                            title={
                              launchable === 0
                                ? 'All selected sessions are completed or cancelled'
                                : `Open ${launchable} active interview${launchable !== 1 ? 's' : ''} in popup windows`
                            }
                          >
                            <PlayCircle className="h-3.5 w-3.5" />
                            {launchable > 1 ? `Launch ${launchable} interviews` : 'Launch interview'}
                          </button>
                        );
                      })()}
                    </div>
                  )}

                  {filteredRows.length === 0 ? (
                    <div className="px-4 py-10 text-center text-sm vscode-text-secondary border-t border-[var(--vscode-border)]">
                      No sessions match your search. Try a different keyword.
                    </div>
                  ) : (
                    <>
                      <div className="ggj-data-table-wrap min-w-0">
                        <table className="ggj-data-table ggj-data-table--wide ggj-data-table--candidates">
                          <thead>
                            <tr>
                              <th className="w-10 px-4 py-3">
                                <input
                                  type="checkbox"
                                  ref={headerCheckRef}
                                  checked={pagedRows.length > 0 && pagedRows.every((r) => selectedIds.has(r.id))}
                                  onChange={() => {
                                    if (pagedRows.every((r) => selectedIds.has(r.id))) {
                                      setSelectedIds((prev) => {
                                        const next = new Set(prev);
                                        for (const r of pagedRows) next.delete(r.id);
                                        return next;
                                      });
                                    } else {
                                      setSelectedIds((prev) => {
                                        const next = new Set(prev);
                                        for (const r of pagedRows) next.add(r.id);
                                        return next;
                                      });
                                    }
                                  }}
                                  className="rounded border-gray-600"
                                  aria-label="Select all on this page"
                                />
                              </th>
                              <th>Candidate</th>
                              <th className="whitespace-nowrap">Session date</th>
                              <th className="whitespace-nowrap">Score</th>
                              <th className="whitespace-nowrap">Status</th>
                              {hasExtra('scheduled_by') && <th className="whitespace-nowrap min-w-[7rem]">Scheduled by</th>}
                              {hasExtra('round') && <th className="whitespace-nowrap">Round</th>}
                              {hasExtra('language') && <th className="whitespace-nowrap">Language</th>}
                              {hasExtra('created_at') && <th className="whitespace-nowrap">Created</th>}
                              {hasExtra('expires_at') && <th className="whitespace-nowrap">Expires</th>}
                              {hasExtra('scheduled_at') && <th className="whitespace-nowrap">Calendar slot</th>}
                              {hasExtra('slot_status') && <th className="whitespace-nowrap">Slot status</th>}
                              {hasExtra('application_id') && <th className="whitespace-nowrap">Application</th>}
                              <th className="whitespace-nowrap text-right">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pagedRows.map((r) => {
                              const name = r.candidate?.full_name?.trim() || '—';
                              const email = r.candidate?.email?.trim();
                              const initiator = r.initiator;
                              const chipKind = resolveReviewerSessionStatus(r.status, r.assessed_at, r.assessment_scores);
                              return (
                                <tr key={r.id} className={selectedIds.has(r.id) ? 'bg-brand-500/10' : ''}>
                                  <td className="px-4 py-3 w-10">
                                    <input
                                      type="checkbox"
                                      checked={selectedIds.has(r.id)}
                                      onChange={() => {
                                        setSelectedIds((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(r.id)) next.delete(r.id);
                                          else next.add(r.id);
                                          return next;
                                        });
                                      }}
                                      className="rounded border-gray-600"
                                      aria-label={`Select ${name}`}
                                    />
                                  </td>
                                  <td>
                                    <Link
                                      to={`/ai-interviews/${r.id}?tab=review`}
                                      className="font-semibold vscode-text-primary hover:text-brand-400 transition-colors"
                                    >
                                      {name}
                                    </Link>
                                    {email && <span className="ggj-table-sub">{email}</span>}
                                  </td>
                                  <td className="text-xs vscode-text-secondary whitespace-nowrap tabular-nums">
                                    {formatDateTimeGmt(reviewerSessionDateIso(r))}
                                  </td>
                                  <td className="font-mono text-sm tabular-nums">
                                    {reviewerListScoreDisplay(r.assessment_scores, r.assessed_at)}
                                  </td>
                                  <td>
                                    <StatusChip
                                      label={reviewerStatusChipLabel(chipKind)}
                                      dotColor={statusDotColorFromHint(reviewerStatusChipLabel(chipKind))}
                                    />
                                  </td>
                                  {hasExtra('scheduled_by') && (
                                    <td className="text-xs">
                                      {initiator ? (
                                        <Link
                                          to={`/team/member/${initiator.id}`}
                                          className="vscode-text-primary hover:text-brand-400 transition-colors font-medium"
                                        >
                                          {initiator.display_name?.trim() || initiator.email || '—'}
                                        </Link>
                                      ) : (
                                        <span className="vscode-text-tertiary italic">—</span>
                                      )}
                                      {initiator?.email && initiator.display_name?.trim() ? (
                                        <span className="ggj-table-sub">{initiator.email}</span>
                                      ) : null}
                                    </td>
                                  )}
                                  {hasExtra('round') && <td className="capitalize">{r.session_type}</td>}
                                  {hasExtra('language') && <td className="font-mono text-xs">{r.language}</td>}
                                  {hasExtra('created_at') && (
                                    <td className="whitespace-nowrap text-xs vscode-text-secondary tabular-nums">
                                      {formatDateTimeGmt(r.created_at)}
                                    </td>
                                  )}
                                  {hasExtra('expires_at') && (
                                    <td className="text-xs vscode-text-secondary whitespace-nowrap tabular-nums">
                                      {r.expires_at ? formatDateTimeGmt(r.expires_at) : '—'}
                                    </td>
                                  )}
                                  {hasExtra('scheduled_at') && (
                                    <td className="text-xs vscode-text-secondary whitespace-nowrap tabular-nums">
                                      {r.pipeline_interview_event?.scheduled_at
                                        ? formatDateTimeGmt(r.pipeline_interview_event.scheduled_at)
                                        : '—'}
                                    </td>
                                  )}
                                  {hasExtra('slot_status') && (
                                    <td className="text-xs capitalize">
                                      {formatPipelineSlotStatus(r.pipeline_interview_event?.status)}
                                    </td>
                                  )}
                                  {hasExtra('application_id') && (
                                    <td className="text-[11px] font-mono vscode-text-tertiary truncate max-w-[8rem]">
                                      {r.application_id ? (
                                        <Link to={`/applications/${r.application_id}`} className="hover:text-brand-400">
                                          {r.application_id.slice(0, 8)}…
                                        </Link>
                                      ) : (
                                        '—'
                                      )}
                                    </td>
                                  )}
                                  <td className="text-right">
                                    <div className="inline-flex flex-wrap items-center justify-end gap-1.5">
                                      <Link
                                        to={`/ai-interviews/${r.id}?tab=review`}
                                        className="btn-secondary text-xs px-2 py-1 inline-flex items-center gap-1"
                                      >
                                        <ExternalLink className="w-3 h-3 shrink-0" aria-hidden />
                                        Review
                                      </Link>
                                      <button
                                        type="button"
                                        className="btn-secondary text-xs px-2 py-1 inline-flex items-center gap-1"
                                        onClick={() => void copyLink(r.access_token)}
                                      >
                                        <Copy className="w-3 h-3 shrink-0" aria-hidden />
                                        Copy link
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <TablePagination
                        page={page}
                        pageSize={pageSize}
                        totalItems={totalItems}
                        onPageChange={setPage}
                        onPageSizeChange={(size) => {
                          setPageSize(size);
                          setPage(1);
                        }}
                      />
                    </>
                  )}
                </div>
              )}
        </>
      )}

      {orgId && user?.orgId && (
        <>
          <AiInterviewScheduleWizardModal
            open={scheduleOpen}
            onClose={() => setScheduleOpen(false)}
            orgId={orgId}
            orgName={user.orgName}
            recruiterName={user.name}
            orgUserId={user.orgUserId}
            applications={applications}
            scheduleDayKey={null}
            initialApplicationId={null}
            onScheduled={() => void load()}
          />
          <AiInterviewsConfigureColumnsModal
            open={configureOpen}
            onClose={() => setConfigureOpen(false)}
            orgId={orgId}
            onSave={() => setTablePolicy(loadAiInterviewsTablePolicy(orgId))}
          />
          <GenericManageRightsModal
            open={manageRightsOpen}
            onClose={() => setManageRightsOpen(false)}
            orgId={orgId}
            entityKey="ai_interviews_sessions"
            subtitle="Per-user access for the AI interviews list — stored locally per browser."
          />
        </>
      )}
    </div>
  );
}
